import React, { useState, useRef } from 'react'
import { useNavigate, Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Loader,
  Panel,
  VStack,
  Heading,
  Form,
  Text,
  PasswordInput,
  ButtonToolbar,
  Button,
  FormInstance,
} from 'rsuite'

import FormField from 'shared/FormField/FormField'
import PasswordRequirements from '../../shared/PasswordRequirements/PasswordRequirements'
import * as passwordValidators from '../../../shared/passwordValidators'
import { useCurrentUser } from 'app/lib/user'

import * as styles from './PasswordReset.m.css'

async function resetPassword(password: string) {
  const response = await fetch('/auth/reset-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password }),
  })

  if (response.ok) {
    return true
  } else {
    return false
  }
}

async function forgotPassword(code: string, password: string) {
  const response = await fetch('/auth/forgot-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code, password }),
  })

  if (response.ok) {
    return true
  } else {
    return false
  }
}

async function validateResetCode(resetCode: string) {
  const response = await fetch(`/auth/reset-codes/${resetCode}`)

  if (response.ok) {
    return true
  } else {
    return false
  }
}

type FormValue = {
  password: string
  confirmPassword: string
}

const params = new URLSearchParams(window.location.search)
const resetCode = params.get('c')

const PasswordReset: React.FC = (): React.ReactElement => {
  const navigate = useNavigate()
  const [formValue, setFormValue] = useState<FormValue>({
    password: '',
    confirmPassword: '',
  })
  const { password, confirmPassword } = formValue
  const [error, setError] = useState<string>('')
  const [passwordResetSuccessful, setPasswordResetSuccessful] =
    useState<boolean>(false)

  const formRef = useRef<FormInstance | null>(null)

  const { data: isValidCode, isLoading: isValidCodeLoading } = useQuery({
    queryKey: ['validForgotCode', resetCode],
    queryFn: () => validateResetCode(resetCode ?? ''),
    enabled: !!resetCode,
    staleTime: Infinity,
  })

  const {
    data: currentUser,
    isError: currentUserError,
    isLoading: isCurrentUserLoading,
  } = useCurrentUser()

  const isForgotPasswordReset = resetCode && isValidCode
  const isAuthedForcedReset = !!currentUser
  const loading = isValidCodeLoading || isCurrentUserLoading

  const isValidPassword =
    passwordValidators.isValidPassword(password) &&
    password === confirmPassword &&
    password.length > 0

  const handleSubmit = async () => {
    const passwordResetSuccess = resetCode
      ? await forgotPassword(resetCode, formValue.password)
      : await resetPassword(formValue.password)

    if (passwordResetSuccess) {
      setPasswordResetSuccessful(true)
      setTimeout(() => {
        navigate('/login', { viewTransition: true })
      }, 5000)
    } else {
      setError(
        'There was an error resetting your password. Please try again later.',
      )
    }

    return false
  }

  // Since this page is used in both an authenticated and unauthenticated
  // context, the page will be stuck in a loading state while the currentUser
  // query fails for unauthenticated users. We're okay with this - it slightly
  // delays someone who would try to guess a guid (presumably because they don't
  // know how bad of an idea that is) but it prevents us from having to maintain
  // two separate pages for forgot password resets and forced password resets.

  return (
    <>
      {loading ? (
        <Loader center backdrop content="Loading..." vertical size="md" />
      ) : (
        <Panel>
          {resetCode && !isValidCode && (
            <VStack spacing={14}>
              <Heading>Invalid password reset link</Heading>
              <Text>
                <Link to="/forgot-password" viewTransition>
                  Click here to request a new one.
                </Link>
              </Text>
            </VStack>
          )}
          {!resetCode && currentUserError && (
            <VStack spacing={4}>
              <Heading>Unauthorized</Heading>
              <Text>
                You must be logged in or have a valid password reset code in
                order to reset your password.{' '}
                <Link to="/forgot-password" viewTransition>
                  Click here to request a password reset.
                </Link>
                . Alternatively,{' '}
                <Link to="/login" viewTransition>
                  click here
                </Link>{' '}
                to return to the login page.
              </Text>
            </VStack>
          )}
          {(isForgotPasswordReset || isAuthedForcedReset) &&
            !passwordResetSuccessful && (
              <Form
                ref={formRef}
                onSubmit={handleSubmit}
                fluid
                onChange={(value) => setFormValue(value as FormValue)}
                formValue={formValue}
              >
                <Text>
                  You are required to reset your password before you can
                  continue.
                </Text>
                <FormField
                  name="password"
                  label="New Password"
                  accepter={PasswordInput}
                />
                <FormField
                  name="confirmPassword"
                  label="Confirm New Password"
                  accepter={PasswordInput}
                />
                <PasswordRequirements
                  password={password}
                  confirmPassword={confirmPassword}
                />
                <ButtonToolbar>
                  <Button
                    appearance="primary"
                    block
                    disabled={!isValidPassword}
                    type="submit"
                  >
                    Reset Password
                  </Button>
                </ButtonToolbar>
                {error.length > 0 && (
                  <div className={styles.error}>{error}</div>
                )}
              </Form>
            )}
          {passwordResetSuccessful && (
            <Text>
              Password reset was successful. You'll need to log in again. You
              will be redirected to the{' '}
              <Link to="/login" viewTransition>
                login page
              </Link>{' '}
              in a few seconds.
            </Text>
          )}
        </Panel>
      )}
    </>
  )
}

export default PasswordReset
